import { OmpCalloutTone, OmpMoment, OmpPalette, OmpSectionKey } from '../../_models';
import { OmpStyleVar } from '../order-management-portal.sections';

// Derives every OMP colour from the brand's theme so a portal looks finished before anyone
// touches it. Brand Settings only defines a handful of colours; the rest are tints, shades and
// readable contrasts of those.

const NEUTRAL_TEXT = '#163549';
const WHITE = '#FFFFFF';

function parseHex(hex: string): number[] {
  let rgb: number[] = null;
  if (typeof hex === 'string') {
    const value = hex.replace('#', '');
    if (value.length >= 6) {
      const r = parseInt(value.substring(0, 2), 16);
      const g = parseInt(value.substring(2, 4), 16);
      const b = parseInt(value.substring(4, 6), 16);
      if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        rgb = [r, g, b];
      }
    }
  }
  return rgb;
}

function toHex(rgb: number[]): string {
  return '#' + rgb.map((part) => {
    const clamped = Math.max(0, Math.min(255, Math.round(part)));
    return ('0' + clamped.toString(16)).slice(-2).toUpperCase();
  }).join('');
}

export function mixColors(hex: string, other: string, weight: number): string {
  const a = parseHex(hex);
  const b = parseHex(other);
  let result = hex;
  if (a && b) {
    result = toHex([0, 1, 2].map((i) => a[i] * weight + b[i] * (1 - weight)));
  }
  return result;
}

function luminance(hex: string): number {
  const rgb = parseHex(hex);
  let value = 1;
  if (rgb) {
    value = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
  }
  return value;
}

export function readableOn(hex: string): string {
  let text = NEUTRAL_TEXT;
  if (luminance(hex) < 0.6) {
    text = WHITE;
  }
  return text;
}

function pick(brand: any, key: string, fallback: string): string {
  let value = fallback;
  if (brand && brand[key] && parseHex(brand[key])) {
    value = brand[key];
  }
  return value;
}

export function brandPalette(brandVariables: any): OmpPalette {
  const theme = pick(brandVariables, OmpStyleVar.ThemeColor, '#5A301C');
  return {
    theme: theme,
    onTheme: readableOn(theme),
    hover: pick(brandVariables, OmpStyleVar.ButtonHoverColor, mixColors(theme, WHITE, 0.75)),
    tintStrong: mixColors(theme, WHITE, 0.28),
    tintSoft: mixColors(theme, WHITE, 0.12),
    tintFaint: mixColors(theme, WHITE, 0.05),
    surface: WHITE,
    text: NEUTRAL_TEXT,
  };
}

export function paletteStyleDefaults(brandVariables: any): { [key: string]: string } {
  const p = brandPalette(brandVariables);
  return {
    [OmpStyleVar.ThemeColor]: p.theme,
    [OmpStyleVar.ButtonTextColor]: p.onTheme,
    [OmpStyleVar.ButtonHoverColor]: p.hover,
    [OmpStyleVar.LinksAndIconsColor]: p.theme,
    [OmpStyleVar.HeaderBackgroundColor]: p.surface,
    [OmpStyleVar.CardBackgroundColor]: p.surface,
    [OmpStyleVar.CardBorderColor]: p.tintStrong,
    [OmpStyleVar.PageBackgroundColor]: p.tintFaint,
    [OmpStyleVar.BodyTextColor]: p.text,
  };
}

const POSITIVE_ACCENT = '#2E7D52';
const CAUTION_ACCENT = '#B07514';

export function calloutToneColors(brandVariables: any, tone: string): { [field: string]: string } {
  const p = brandPalette(brandVariables);
  let accent = p.theme;
  let background = mixColors(p.theme, WHITE, 0.12);
  if (tone === OmpCalloutTone.Positive) {
    accent = POSITIVE_ACCENT;
    background = mixColors(POSITIVE_ACCENT, WHITE, 0.1);
  } else if (tone === OmpCalloutTone.Caution) {
    accent = CAUTION_ACCENT;
    background = mixColors(CAUTION_ACCENT, WHITE, 0.12);
  } else if (tone === OmpCalloutTone.Plain) {
    background = p.surface;
  }
  return {
    background_color: background,
    accent_color: accent,
    border_color: mixColors(accent, WHITE, 0.28),
  };
}

const OmpMomentStripColors: { [moment: number]: string } = {
  [OmpMoment.SubscriptionActive]: '#D8F8EC',
  [OmpMoment.OrderPending]: '#FFF5EC',
  [OmpMoment.OrderShipped]: '#D9F5ED',
  [OmpMoment.PaymentIssue]: '#FFF2F2',
  [OmpMoment.SubscriptionCancelled]: '#E1E1E0',
  [OmpMoment.Trial]: '#EAF3FC',
};

export function momentStripColor(brandVariables: any, moment: number): string {
  let color = brandPalette(brandVariables).tintSoft;
  if (OmpMomentStripColors[moment]) {
    color = OmpMomentStripColors[moment];
  }
  return color;
}

export function paletteSectionDefaults(
  brandVariables: any,
  sectionKey?: string,
  calloutTone?: string,
  moment?: number
): { [field: string]: string } {
  const p = brandPalette(brandVariables);
  const shared: { [field: string]: string } = {
    banner_background_color: p.theme,
    offer_strip_background_color: p.theme,
    offer_strip_text_color: p.onTheme,
    background_color: p.surface,
    border_color: p.tintStrong,
    faq_background_color: p.tintSoft,
    accent_color: p.theme,
  };
  const overrides = paletteSectionOverrides(brandVariables, calloutTone, moment);
  const forSection = sectionKey ? overrides[sectionKey] : null;
  if (forSection) {
    Object.keys(forSection).forEach((field) => {
      shared[field] = forSection[field];
    });
  }
  return shared;
}

function paletteSectionOverrides(
  brandVariables: any,
  calloutTone?: string,
  moment?: number
): { [key: string]: { [field: string]: string } } {
  const tone = calloutTone ? calloutTone : OmpCalloutTone.Brand;
  return {
    [OmpSectionKey.Callout]: calloutToneColors(brandVariables, tone),
    [OmpSectionKey.MomentCard]: {
      offer_strip_background_color: momentStripColor(brandVariables, moment),
    },
  };
}
